import { useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../../api';
import { go } from '../../App';
import { useLoad } from '../../data';
import { PageHead, Panel, carryToast, fmtMoney, formatDay, mulMoney, toLocalIso, zoneDate, zoneOffset } from '../../ui';
import { Account, AvailabilityDay, Booking, Product } from '../../types';

// H1 — one page, never a wizard: a concierge is standing at a desk with a
// guest in front of them. Availability before the promise; margin shown
// quietly; the aside is the hero — the one ink surface owns "Guest pays".
export function NewBooking() {
  const catalogue = useLoad<{ items: Product[] }>('/products');
  const products = catalogue.data ? catalogue.data.items : [];
  const [productId, setProductId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [guestName, setGuestName] = useState('');
  const [guestRoom, setGuestRoom] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const product = products.find((p) => p.id === productId);
  const account = useLoad<Account>('/account');

  useEffect(() => {
    if (!productId && catalogue.data?.items[0]) setProductId(catalogue.data.items[0].id);
  }, [catalogue.data, productId]);

  // The 7-day strip starts on the PROPERTY's today, not the browser's UTC
  // day — at 01:00 Athens the strip must not begin on yesterday.
  const tzForStrip = account.data?.timezone ?? 'Europe/Athens';
  const todayStr = zoneDate(tzForStrip, Date.now());
  const availability = useLoad<{ days: AvailabilityDay[] }>(
    productId
      ? `/products/${productId}/availability?from=${zoneDate(tzForStrip, Date.now())}&to=${zoneDate(tzForStrip, Date.now() + 6 * 86_400_000)}`
      : null,
  );
  const days = availability.data ? availability.data.days : [];

  // Availability before the promise (canon 12): the answer comes from the API
  // for the EXACT date picked — the free-typed date field and any day beyond the
  // 7-day strip are checked here too, not only the strip's disabled cells.
  const chosenAvail = useLoad<{ days: AvailabilityDay[] }>(
    productId && date ? `/products/${productId}/availability?from=${date}&to=${date}` : null,
  );
  const chosenDay = chosenAvail.data?.days?.[0];
  const checkingAvail = Boolean(date && productId) && chosenAvail.data === null && chosenAvail.error === '';
  // The availability gate must fail CLOSED: if the read errored we cannot vouch
  // for the day, so Send is held (never promise a day we couldn't verify).
  const chosenUnverified = Boolean(date && productId) && chosenAvail.data === null && chosenAvail.error !== '';
  const chosenBlocked = chosenDay?.available === false;
  const isPast = Boolean(date) && date < todayStr;
  // The nearest free day from the loaded strip, offered when the pick is blocked (H1 edge).
  const nearestFree =
    days.find((d) => d.available && d.date > date)?.date ?? days.find((d) => d.available)?.date;

  const units = product?.per === 'guest' ? adults + children : 1;
  const supplierAmount = useMemo(
    () => (product ? mulMoney(product.supplierPrice, units) : undefined),
    [product, units],
  );
  const guestTotal = product?.guestPrice ? mulMoney(product.guestPrice, units) : undefined;
  const marginTotal = product?.margin ? mulMoney(product.margin, units) : undefined;

  const send = async () => {
    if (!product || !date || !supplierAmount || !account.data) return;
    if (isPast || chosenBlocked || checkingAvail || chosenUnverified) return; // availability before the promise
    setBusy(true);
    setError('');
    try {
      // The property's offset, derived from its IANA zone at the date being
      // booked — start and end each get the offset in force at their own
      // moment, so a job crossing a DST boundary keeps honest wall-clock
      // times. End is real date arithmetic — an evening job crosses midnight
      // into the next day, it never wraps back before its own start.
      const tz = account.data.timezone;
      const offset = zoneOffset(tz, Date.parse(`${date}T12:00:00Z`));
      const start = `${date}T${time}:00${offset}`;
      const durMs = Math.max(1, Math.round(product.durationHours ?? 1)) * 3600_000;
      const endMs = Date.parse(start) + durMs;
      const booking = await api<Booking>('POST', '/bookings', {
        productId: product.id,
        supplierId: product.supplierId,
        start,
        end: toLocalIso(new Date(endMs), zoneOffset(tz, endMs)),
        guests: { adults, children },
        guestName,
        guestRoom: guestRoom || undefined,
        notes: notes || undefined,
        supplierAmount,
        idempotencyKey: `web-${crypto.randomUUID()}`,
      });
      // H1 step 5: Requested, with the retract toast (DECISION-042) shown
      // where the hotel lands.
      carryToast(
        booking,
        `Requested · ${product.name}`,
        `${product.supplierName ?? 'The supplier'} is being told — you can unsend for 30 seconds.`,
      );
      go(`/bookings/${booking.id}`);
    } catch (e) {
      const err = e as ApiError;
      setError(err.code === 'price_mismatch' ? 'The supplier has changed this price. Reload and try again.' : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHead title="New booking" sub={account.data?.name ?? ''}>
        <button className="btn" disabled={busy || !date || !guestName || !product || !account.data || isPast || chosenBlocked || checkingAvail || chosenUnverified} onClick={send}>
          {busy ? 'Sending…' : 'Send to supplier'}
        </button>
      </PageHead>

      <div className="nb-grid">
        <div>
          <Panel title="Experience" meta={`${products.length} in catalogue`}>
            {products.map((p) => (
              <button key={p.id} className={`prow ${p.id === productId ? 'selected' : ''}`} onClick={() => setProductId(p.id)}>
                <span className="thumb">{p.kind === 'transfer' ? 'car' : 'sea'}</span>
                <span>
                  <span className="n">{p.name}</span>
                  <br />
                  <span className="m">
                    {p.supplierName} · {p.durationHours} h · max {p.maxGuests}
                  </span>
                </span>
                <span className="p">
                  <span className="val">{fmtMoney(p.guestPrice ?? p.supplierPrice)}</span>
                  <br />
                  <span className="per">{p.per === 'guest' ? 'per person' : 'per job'}</span>
                </span>
                {p.id === productId && <span className="check">✓</span>}
              </button>
            ))}
            {!catalogue.data && !catalogue.error && <p className="hint">Loading the catalogue…</p>}
            {catalogue.data && products.length === 0 && (
              <p className="hint">
                No supplier products yet.{' '}
                <button type="button" onClick={() => go('/suppliers')} style={{ background: 'none', border: 'none', color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>
                  Invite a supplier
                </button>{' '}
                to build your catalogue.
              </p>
            )}
            {catalogue.error && <p className="error-line" role="alert">{catalogue.error}</p>}
          </Panel>

          <Panel title="When">
            <div className="row">
              <div className="field">
                <label htmlFor="date">Date</label>
                <input id="date" type="date" className="mono" min={todayStr} value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="time">Departure</label>
                <input id="time" type="time" className="mono" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="adults">Guests</label>
                <div className="stepper">
                  <button type="button" aria-label="Fewer guests" onClick={() => setAdults(Math.max(1, adults - 1))}>−</button>
                  <input id="adults" type="number" min={1} max={product?.maxGuests ?? 99} value={adults} onChange={(e) => setAdults(Number(e.target.value))} />
                  <button type="button" aria-label="More guests" onClick={() => setAdults(Math.min(product?.maxGuests ?? 99, adults + 1))}>+</button>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="field" style={{ maxWidth: 180 }}>
                <label htmlFor="children">Children</label>
                <input id="children" type="number" min={0} value={children} onChange={(e) => setChildren(Number(e.target.value))} />
              </div>
            </div>
            {isPast && (
              <p className="error-line" role="alert">That date has passed — pick a day from today on.</p>
            )}
            {!isPast && chosenBlocked && (
              <p className="error-line" role="alert">
                {product?.supplierName ?? 'The supplier'} isn&rsquo;t available on {formatDay(date)}.
                {nearestFree && (
                  <>
                    {' '}
                    <button type="button" onClick={() => setDate(nearestFree)} style={{ background: 'none', border: 'none', color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>
                      Try {formatDay(nearestFree)}
                    </button>
                  </>
                )}
              </p>
            )}
            {!isPast && chosenUnverified && (
              <p className="error-line" role="alert">Couldn&rsquo;t check {product?.supplierName ?? 'the supplier'}&rsquo;s availability just now — try again before sending.</p>
            )}
            {!isPast && !chosenBlocked && checkingAvail && <p className="hint">Checking availability…</p>}
          </Panel>

          <Panel title="Guest">
            <div className="row">
              <div className="field">
                <label htmlFor="guestName">Lead name</label>
                <input id="guestName" value={guestName} onChange={(e) => setGuestName(e.target.value)} required />
              </div>
              <div className="field" style={{ maxWidth: 140 }}>
                <label htmlFor="guestRoom">Room</label>
                <input id="guestRoom" className="mono" value={guestRoom} onChange={(e) => setGuestRoom(e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="notes">Note for the supplier</label>
              <textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </Panel>
          {error && <p className="error-line" role="alert">{error}</p>}
        </div>

        <div className="aside-stack">
          <div className="hero">
            <div className="eyebrow">Guest pays</div>
            <div className="figure">{guestTotal ? fmtMoney(guestTotal) : '—'}</div>
            <div className="breakdown">
              {product && (
                <div className="brow">
                  <span className="l">
                    {units} × {fmtMoney(product.guestPrice ?? product.supplierPrice)}
                  </span>
                  <span className="v">{guestTotal ? fmtMoney(guestTotal) : '—'}</span>
                </div>
              )}
              <div className="brow">
                <span className="l">Supplier receives</span>
                <span className="v">{supplierAmount ? fmtMoney(supplierAmount) : '—'}</span>
              </div>
              <div className="brow">
                <span className="l">Hotel margin</span>
                <span className="v olive">{marginTotal ? fmtMoney(marginTotal) : '—'}</span>
              </div>
            </div>
          </div>

          <Panel>
            <div className="supplier-line">
              <span className="avatar">{(product?.supplierName ?? 'S').slice(0, 1)}</span>
              <span>
                <span className="n">{product?.supplierName ?? 'Supplier'}</span>
                <br />
                <span className="s">{product ? `${product.kind === 'transfer' ? 'Transfers' : 'Experiences'} · Rhodes` : ''}</span>
              </span>
            </div>
            <hr className="divider" />
            <div className="avail-label">Availability, next 7 days</div>
            <div className="avail7">
              {days.map((d) => (
                <button key={d.date} type="button" className={`cell ${date === d.date ? 'picked' : ''}`} disabled={!d.available} onClick={() => setDate(d.date)} title={formatDay(d.date)}>
                  <div className={`bar ${d.available ? 'free' : 'blocked'}`} />
                  <div className="d">{formatDay(d.date).slice(0, 3)}</div>
                </button>
              ))}
            </div>
            <div className="legend">
              <span><span className="sw" style={{ background: 'var(--color-success)' }} />Free</span>
              <span><span className="sw" style={{ background: 'var(--border-medium)' }} />Blocked</span>
            </div>
            {availability.error && (
              <p className="hint" style={{ marginTop: 8 }}>
                Availability didn&rsquo;t load — the date field above still works.
              </p>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
